<script setup lang="ts">
import { computed, nextTick, ref, shallowRef, useTemplateRef, watch } from 'vue'
import { storeToRefs } from 'pinia'
import { useMediaLifecycle } from '@/composables/useMediaLifecycle'
import { useMediaSoundPreference } from '@/composables/useMediaSoundPreference'
import { DISTRIBUTE_RADIO_COPY } from '@/constants/distribute'
import { useFeedPreferencesStore } from '@/stores/feedPreferences'
import { stripMarkup } from '@/utils/distribute/format'
import { buildImageBlurUrl, buildImageScaleUrl } from '@/utils/distribute/media-url'
import CoverMetaOverlay from '@/components/distribute-cover-meta-overlay'
import MediaSoundToggle from '@/components/distribute-media-sound-toggle'
import type { Emits, Props } from './type'

defineOptions({
  name: 'DistributeRadioCard',
})

const props = defineProps<Props>()
const emit = defineEmits<Emits>()
const feedPreferencesStore = useFeedPreferencesStore()
const { isDiscoverAutoplayEnabled } = storeToRefs(feedPreferencesStore)

const RADIO_COVER_IMAGE_SIZE = 360
const RADIO_BACKDROP_IMAGE_WIDTH = 120
const RADIO_BACKDROP_IMAGE_HEIGHT = 160
const RADIO_BACKDROP_BLUR_RADIUS = 50

const rootElement = ref<HTMLDivElement | null>(null)
const audioElement = ref<HTMLAudioElement | null>(null)
const backdropImageElement = useTemplateRef<HTMLImageElement>('backdropImage')
const currentPlaybackTime = shallowRef(0)
const isPreviewing = shallowRef(false)
const hasEmittedPreviewReady = shallowRef(false)
const hasBackdropLoaded = shallowRef(false)
const { isPlaybackMuted, toggleSound } = useMediaSoundPreference()

const coverImageUrl = computed(() =>
  buildImageScaleUrl(props.model.coverUrl, {
    height: RADIO_COVER_IMAGE_SIZE,
    width: RADIO_COVER_IMAGE_SIZE,
  }),
)
const backdropImageUrl = computed(() =>
  buildImageBlurUrl(props.model.coverUrl, {
    height: RADIO_BACKDROP_IMAGE_HEIGHT,
    radius: RADIO_BACKDROP_BLUR_RADIUS,
    width: RADIO_BACKDROP_IMAGE_WIDTH,
  }),
)
const previewLoading = computed(() => (props.priority ? 'eager' : 'lazy'))
const previewFetchPriority = computed(() => (props.priority ? 'high' : 'auto'))

interface LyricScene {
  key: string
  line: string
  startTime: number
  endTime: number
}

interface LyricRow {
  key: string
  line: string
  tone: 'primary' | 'secondary'
}

function parseRadioTime(value: string | null | undefined): number | null {
  if (!value) {
    return null
  }

  const parts = value.split(':').map((part) => Number(part))

  if (parts.some((part) => Number.isNaN(part))) {
    return null
  }

  if (parts.length === 4) {
    const [hours = 0, minutes = 0, seconds = 0, milliseconds = 0] = parts

    return hours * 3600 + minutes * 60 + seconds + milliseconds / 1000
  }

  if (parts.length === 3) {
    const [hours = 0, minutes = 0, seconds = 0] = parts

    return hours * 3600 + minutes * 60 + seconds
  }

  return null
}

const fallbackLyrics = computed<[string, string]>(() => [
  stripMarkup(
    props.model.item.summaryMarkup || props.model.item.summary || props.model.item.name,
  ) || DISTRIBUTE_RADIO_COPY.fallbackLine,
  props.model.item.name || DISTRIBUTE_RADIO_COPY.fallbackTitle,
])

const lyricTimeline = computed<LyricScene[]>(() => {
  let cursor = parseRadioTime(props.model.item.radio?.beginTime) ?? 0

  return (props.model.item.radio?.radioScenes ?? [])
    .map((scene, index) => {
      const line = stripMarkup(scene.lines)

      if (!line) {
        return null
      }

      const startTime = parseRadioTime(scene.beginTime) ?? cursor
      const durationSeconds = scene.duration ? Math.max(0, scene.duration / 1000) : 0
      const parsedEndTime = parseRadioTime(scene.endTime)
      const endTime =
        parsedEndTime !== null && parsedEndTime > startTime
          ? parsedEndTime
          : startTime + (durationSeconds || 2)

      cursor = endTime

      return {
        key: scene.id || `${scene.sn ?? index}-${line}`,
        line,
        startTime,
        endTime,
      }
    })
    .filter((scene): scene is LyricScene => Boolean(scene))
})

const previewStartTime = computed<number | null>(
  () => parseRadioTime(props.model.item.radio?.beginTime) ?? lyricTimeline.value[0]?.startTime ?? 0,
)

const previewEndTime = computed<number | null>(
  () =>
    parseRadioTime(props.model.item.radio?.endTime) ??
    lyricTimeline.value[lyricTimeline.value.length - 1]?.endTime ??
    null,
)
const shouldRenderAudio = computed(() => Boolean(props.model.previewAudioUrl))
const canAutoplayPreview = computed(() => isDiscoverAutoplayEnabled.value && props.autoplayActive)

const activeLyricIndex = computed(() => {
  const timeline = lyricTimeline.value

  if (!timeline.length) {
    return -1
  }

  const exactIndex = timeline.findIndex(
    (scene) =>
      currentPlaybackTime.value >= scene.startTime && currentPlaybackTime.value < scene.endTime,
  )

  if (exactIndex >= 0) {
    return exactIndex
  }

  const nextIndex = timeline.findIndex((scene) => currentPlaybackTime.value < scene.startTime)

  if (nextIndex >= 0) {
    return nextIndex
  }

  return timeline.length - 1
})

const visibleLyrics = computed<LyricRow[]>(() => {
  const timeline = lyricTimeline.value

  if (!timeline.length || activeLyricIndex.value < 0) {
    return [
      { key: 'fallback-primary', line: fallbackLyrics.value[0], tone: 'primary' },
      { key: 'fallback-secondary', line: fallbackLyrics.value[1], tone: 'secondary' },
    ]
  }

  const activeScene = timeline[activeLyricIndex.value]
  const nextScene =
    timeline.length > 1 ? timeline[(activeLyricIndex.value + 1) % timeline.length] : undefined

  return [
    {
      key: `primary-${activeScene?.key ?? 'fallback'}`,
      line: activeScene?.line || fallbackLyrics.value[0],
      tone: 'primary',
    },
    {
      key: `secondary-${nextScene?.key ?? 'fallback'}`,
      line: nextScene?.line || fallbackLyrics.value[1],
      tone: 'secondary',
    },
  ]
})

const lyricPanelKey = computed(() => visibleLyrics.value.map((lyric) => lyric.key).join('|'))

const {
  isPlaying,
  isVisible: isMediaVisible,
  play,
  stop,
} = useMediaLifecycle(audioElement, {
  autoPlayWhenVisible: canAutoplayPreview,
  endTime: previewEndTime,
  loopSegment: true,
  muted: isPlaybackMuted,
  onTimeUpdate: handleAudioTimeUpdate,
  restartOnPlay: true,
  startTime: previewStartTime,
  visibilityTarget: rootElement,
})
const isContentActive = computed(() => isPreviewing.value || isPlaying.value)

watch(
  previewStartTime,
  (startTime) => {
    if (!isPreviewing.value) {
      currentPlaybackTime.value = startTime ?? 0
    }
  },
  { immediate: true },
)

watch(
  coverImageUrl,
  (nextCoverUrl) => {
    hasEmittedPreviewReady.value = false

    if (!nextCoverUrl) {
      emitPreviewReady()
    }
  },
  { immediate: true },
)

watch(
  backdropImageUrl,
  async (nextBackdropUrl) => {
    hasBackdropLoaded.value = !nextBackdropUrl

    if (!nextBackdropUrl) {
      return
    }

    await nextTick()

    if (backdropImageElement.value?.complete && backdropImageElement.value.naturalWidth > 0) {
      hasBackdropLoaded.value = true
    }
  },
  { immediate: true },
)

watch(
  shouldRenderAudio,
  async (renderAudio) => {
    if (!renderAudio || !isPreviewing.value) {
      return
    }

    await nextTick()
    void play()
  },
  { flush: 'post' },
)

function handleAudioTimeUpdate(currentTime: number) {
  if (isContentActive.value) {
    currentPlaybackTime.value = currentTime
  }
}

function handleBackdropLoad() {
  hasBackdropLoaded.value = true
}

function handleMouseEnter() {
  isPreviewing.value = true
  currentPlaybackTime.value = previewStartTime.value ?? currentPlaybackTime.value
  void play()
}

function handleMouseLeave() {
  isPreviewing.value = false
  stop()

  if (!canAutoplayPreview.value || !isMediaVisible.value) {
    currentPlaybackTime.value = previewStartTime.value ?? currentPlaybackTime.value
  }
}

function handleToggleSound() {
  toggleSound()
  isPreviewing.value = true
  currentPlaybackTime.value = previewStartTime.value ?? currentPlaybackTime.value
  void play()
}

function emitPreviewReady() {
  if (hasEmittedPreviewReady.value) {
    return
  }

  hasEmittedPreviewReady.value = true
  emit('preview-ready')
}
</script>

<template>
  <div
    ref="rootElement"
    class="radio-card"
    @mouseenter="handleMouseEnter"
    @mouseleave="handleMouseLeave"
  >
    <audio
      v-if="shouldRenderAudio"
      ref="audioElement"
      :src="model.previewAudioUrl ?? undefined"
      preload="metadata"
    />

    <MediaSoundToggle
      v-if="model.previewAudioUrl"
      :muted="isPlaybackMuted"
      @toggle="handleToggleSound"
    />

    <div class="radio-card__backdrop" :class="{ 'is-loaded': hasBackdropLoaded }">
      <img
        v-if="backdropImageUrl"
        ref="backdropImage"
        class="radio-card__backdrop-image"
        :src="backdropImageUrl"
        alt=""
        decoding="async"
        @load="handleBackdropLoad"
      />
    </div>
    <div class="radio-card__glass" :class="{ 'is-loaded': hasBackdropLoaded }" />

    <div class="radio-card__content">
      <img
        v-if="coverImageUrl"
        class="radio-card__cover"
        :src="coverImageUrl"
        :alt="model.item.name || 'Radio cover'"
        :loading="previewLoading"
        :fetchpriority="previewFetchPriority"
        decoding="async"
        @load="emitPreviewReady"
      />

      <div class="radio-card__lyrics">
        <Transition name="radio-lyric-roll">
          <div :key="lyricPanelKey" class="radio-card__lyrics-stack">
            <p
              v-for="lyric in visibleLyrics"
              :key="lyric.key"
              class="radio-card__lyric"
              :class="`radio-card__lyric--${lyric.tone}`"
            >
              {{ lyric.line }}
            </p>
          </div>
        </Transition>
      </div>
    </div>

    <CoverMetaOverlay :model="model" :require-login="requireLogin" @login="$emit('login')" />
  </div>
</template>

<style scoped>
.radio-card {
  position: relative;
  container-type: inline-size;
  overflow: hidden;
  aspect-ratio: 3 / 4;
  border-radius: 12px;
  background: #181818;
  --radio-content-inset: 16px;
  --radio-content-bottom-safe: 52px;
  --radio-content-gap: 16px;
  --radio-primary-font-size: 18px;
  --radio-primary-line-height: 22px;
  --radio-secondary-font-size: 14px;
  --radio-secondary-line-height: 18px;
  --radio-lyrics-gap: 8px;
  --radio-lyrics-reserved-height: calc(
    var(--radio-primary-line-height) + var(--radio-primary-line-height) +
      var(--radio-secondary-line-height) + var(--radio-secondary-line-height) +
      var(--radio-lyrics-gap)
  );
}

.radio-card__backdrop,
.radio-card__glass {
  position: absolute;
  inset: 0;
  opacity: 0;
  transition: opacity 0.18s ease;
  will-change: opacity;
}

.radio-card__backdrop {
  overflow: hidden;
}

.radio-card__backdrop.is-loaded,
.radio-card__glass.is-loaded {
  opacity: 1;
}

.radio-card__backdrop-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  filter: blur(25px);
  transform: scale(1.16);
}

.radio-card__glass {
  background: rgba(0, 0, 0, 0.2);
  backdrop-filter: blur(25px);
}

.radio-card:hover :deep(.media-sound-toggle) {
  opacity: 1;
  pointer-events: auto;
}

.radio-card:hover :deep(.cover-meta__bgm),
.radio-card:focus-within :deep(.cover-meta__bgm) {
  animation-play-state: running;
}

.radio-card__content {
  position: absolute;
  top: var(--radio-content-inset);
  right: var(--radio-content-inset);
  bottom: var(--radio-content-bottom-safe);
  left: var(--radio-content-inset);
  z-index: 1;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: var(--radio-content-gap);
  pointer-events: none;
}

.radio-card__cover {
  width: min(200px, 94%);
  aspect-ratio: 1;
  border-radius: 8px;
  object-fit: cover;
  box-shadow: 0 0 20px rgba(0, 0, 0, 0.1);
}

.radio-card__lyrics {
  position: relative;
  width: 100%;
  height: var(--radio-lyrics-reserved-height);
  overflow: hidden;
  text-align: left;
}

.radio-card__lyrics-stack {
  position: absolute;
  inset: 0 4px;
  display: flex;
  flex-direction: column;
  gap: var(--radio-lyrics-gap);
}

.radio-card__lyric {
  display: -webkit-box;
  width: 100%;
  margin: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: normal;
  font-size: 14px;
  font-weight: 700;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
}

.radio-card__lyric--primary {
  color: #fff;
  font-size: var(--radio-primary-font-size);
  line-height: var(--radio-primary-line-height);
}

.radio-card__lyric--secondary {
  color: rgba(255, 255, 255, 0.3);
  font-size: var(--radio-secondary-font-size);
  line-height: var(--radio-secondary-line-height);
}

.radio-lyric-roll-enter-active,
.radio-lyric-roll-leave-active {
  transition:
    opacity 0.34s ease,
    transform 0.34s cubic-bezier(0.22, 1, 0.36, 1),
    filter 0.34s ease;
  will-change: opacity, transform, filter;
}

.radio-lyric-roll-enter-from {
  opacity: 0;
  filter: blur(8px);
  transform: translate3d(0, 18px, 0);
}

.radio-lyric-roll-leave-to {
  opacity: 0;
  filter: blur(8px);
  transform: translate3d(0, -18px, 0);
}

.radio-lyric-roll-enter-to,
.radio-lyric-roll-leave-from {
  opacity: 1;
  filter: blur(0);
  transform: translate3d(0, 0, 0);
}

.radio-lyric-roll-leave-active {
  position: absolute;
}

@supports (width: 1cqw) {
  .radio-card {
    --radio-content-gap: clamp(8px, 4cqw, 14px);
    --radio-primary-font-size: clamp(16px, 6.6cqw, 18px);
    --radio-primary-line-height: clamp(19px, 8cqw, 22px);
    --radio-secondary-font-size: clamp(12px, 5cqw, 14px);
    --radio-secondary-line-height: clamp(15px, 6.5cqw, 18px);
    --radio-lyrics-gap: clamp(6px, 3cqw, 8px);
  }

  .radio-card__cover {
    width: min(
      200px,
      100%,
      calc(
        133.333cqw - var(--radio-content-inset) - var(--radio-content-bottom-safe) -
          var(--radio-content-gap) - var(--radio-lyrics-reserved-height)
      )
    );
  }
}
</style>
